/**
 * The merged rock mass of the Medium tier (lifted from the dungeon-floors
 * prototype's `dungeon-rock-mass.js`, with the terrace heights and the
 * distance fade of `dungeon-world.js`). Every wall cell is a closed prism on
 * its grid cell; exposed cells lean, step back in a strata ledge and rise to
 * a sloping shoulder whose corners are shared with their neighbours, so the
 * tops form one smooth geological height field instead of per-tile boxes.
 * Beyond the map edge counts as solid rock. Rear rock rises in terraces with
 * depth behind the open face; rock far from walkable ground fades toward
 * `capFadeFloorPercent` of its albedo (per vertex). One merged geometry, drawn
 * as a single identity instance so it shares the instanced surface program.
 * The Off tier keeps slice 1's wall boxes (the Off numbers are unchanged).
 */
import type { Theme } from '@ab/content-schema';
import type { Floor } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { strataWarp, strataY } from './geology.ts';
import { isOpen, tileIndex } from './grid.ts';

type Cell = { readonly x: number; readonly y: number; readonly height: number; vertexStart: number; vertexCount: number };
type CornerTerms = { n: readonly [number, number]; v: number; bed: number; f63: number; f13: number; f22: number; f31: number; jit: number };
type Point = readonly [number, number, number];

export type RockMass = {
  readonly geometry: THREE.BufferGeometry;
  /** Height of the highest upward rock triangle at a world point, or null where there is no rock top. */
  readonly topAt: (x: number, z: number) => number | null;
};

const k = presentation.medium.rock;

/** Medium wall heights: low stubs south of open ground and along the camera-side map edge, rear terraces behind. */
const heightOf = (floor: Floor, x: number, y: number): number => {
  const open = (dx: number, dy: number): boolean => isOpen(floor, x + dx, y + dy);
  const cutaway = open(0, 1) || open(0, 2);
  const openRow = (dy: number): boolean => k.terraceColumns.some((dx) => open(dx, dy));
  const front = [1, 2, 3].find((dy) => open(0, -dy));
  const behind = [3, 4].find(openRow);
  const side = open(-1, 0) || open(1, 0);
  const tier = side ? 0 : Math.min(front !== undefined ? front - 1 : 2, behind === 3 ? 0 : behind === 4 ? 1 : 2);
  const southEdge = y <= 2 && !Array.from({ length: y }, (_, row) => row).some((row) => [-1, 0, 1].some((dx) => isOpen(floor, x + dx, row)));
  return cutaway || southEdge ? k.stubHeight : k.wallHeight + k.terraceStep * tier;
};

/** Albedo factor per world point: 1 near walkable ground, falling to the theme's floor far from it; null when the theme does not fade. */
const fadeField = (floor: Floor, m: Theme['tileMaterials']): ((x: number, z: number) => number) | null => {
  const low = m.capFadeFloorPercent / 100;
  if (low >= 1) return null;
  const near = m.capFadeNearPercent / 100;
  const far = m.capFadeFarPercent / 100;
  const { width: w, height: h } = floor;
  const dist = new Float32Array(w * h).fill(1e9);
  const queue: number[] = [];
  for (let i = 0; i < w * h; i += 1) {
    if (isOpen(floor, i % w, Math.floor(i / w))) {
      dist[i] = 0;
      queue.push(i);
    }
  }
  for (let head = 0; head < queue.length; head += 1) {
    const i = queue[head] ?? 0;
    const x = i % w;
    const y = Math.floor(i / w);
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const n = ny * w + nx;
        const d = (dist[i] ?? 0) + 1;
        if ((dist[n] ?? 0) > d) {
          dist[n] = d;
          queue.push(n);
        }
      }
    }
  }
  const at = (x: number, y: number): number => dist[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))] ?? 0;
  return (x, z) => {
    const gy = -z;
    const x0 = Math.floor(x);
    const y0 = Math.floor(gy);
    const tx = x - x0;
    const ty = gy - y0;
    const d = (at(x0, y0) * (1 - tx) + at(x0 + 1, y0) * tx) * (1 - ty) + (at(x0, y0 + 1) * (1 - tx) + at(x0 + 1, y0 + 1) * tx) * ty;
    return 1 - (1 - low) * Math.min(1, Math.max(0, (d - near) / (far - near)));
  };
};

export const buildRockMass = (floor: Floor, seed: number, theme: Theme, wallTint: THREE.Color): RockMass => {
  const cells: Cell[] = [];
  for (let y = 0; y < floor.height; y += 1) {
    for (let x = 0; x < floor.width; x += 1) if (!isOpen(floor, x, y)) cells.push({ x, y, height: heightOf(floor, x, y), vertexStart: 0, vertexCount: 0 });
  }
  const fade = fadeField(floor, theme.tileMaterials);
  const cap = cells.length * k.maxTrianglesPerCell * 3;
  const positions = new Float32Array(cap * 3);
  const tops = new Float32Array(cap);
  const colors = new Float32Array(cap * 3);
  let count = 0;
  const s = (seed >>> 0) % k.seedPeriod;
  const solid = (x: number, z: number): boolean => !isOpen(floor, Math.round(x), Math.round(-z));
  const field = (x: number, z: number): number => 0.5 + 0.5 * Math.sin(x * 0.77 + z * 0.53 + s * 0.13 + Math.sin(x * 0.21 - z * 0.38) * 1.3);
  const bed = (x: number, z: number): number => 0.32 * strataWarp(x * 1.7 + (s % 19), z * 1.7) + 0.12 * Math.sin(x * 1.51 + z * 0.8) + 0.06 * strataY(Math.sin(x * 0.23 - z * 0.37));
  const direction = (x: number, z: number): readonly [number, number] => {
    const around: [number, number][] = [];
    for (const dx of [-0.5, 0.5]) for (const dz of [-0.5, 0.5]) if (solid(x + dx, z + dz)) around.push([dx, dz]);
    // A concave triple junction belongs to three cells: holding its corner keeps each top on its own footprint.
    if (around.length >= 3) return [0, 0];
    return [Math.sign(around.reduce((sum, c) => sum + c[0], 0)), Math.sign(around.reduce((sum, c) => sum + c[1], 0))];
  };
  const cornerHash = (x: number, z: number): number => {
    let n = Math.imul(Math.round(x * 2) + 7919, 374761393) ^ Math.imul(Math.round(z * 2) + 104729, 668265263) ^ Math.imul(s + 1, 1274126177);
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
  };
  const corners4 = new Map<number, CornerTerms>();
  const corner = (x: number, z: number): CornerTerms => {
    const key = (x + 0.5) * 65536 + (0.5 - z);
    let q = corners4.get(key);
    if (q === undefined) {
      q = { n: direction(x, z), v: field(x, z), bed: bed(x, z), f63: field(x * 0.63 + 4, z * 0.63), f13: field(x + 1.3, z + 2.1), f22: field(x - 2.2, z + 0.7), f31: field(x + 3.1, z - 1.7), jit: cornerHash(x, z) };
      corners4.set(key, q);
    }
    return q;
  };
  const tint = new THREE.Color();
  const put = (p: Point, top: number): void => {
    positions.set(p, count * 3);
    tops[count] = top;
    const f = fade === null ? 1 : fade(p[0], p[2]);
    tint.copy(wallTint).multiplyScalar(f);
    colors.set([tint.r, tint.g, tint.b], count * 3);
    count += 1;
  };
  const noTop: number = presentation.medium.surfaces.noTop;
  const triangle = (a: Point, b: Point, c: Point, ta = noTop, tb = noTop, tc = noTop): void => {
    put(a, ta);
    put(b, tb);
    put(c, tc);
  };
  const quad: readonly (readonly [number, number])[] = [
    [-0.5, -0.5],
    [0.5, -0.5],
    [0.5, 0.5],
    [-0.5, 0.5],
  ];
  for (const cell of cells) {
    cell.vertexStart = count;
    const cx = cell.x;
    const cz = -cell.y;
    const exposed = quad.some(([dx, dz]) => corner(cx + dx, cz + dz).n.some((v) => v !== 0));
    const low = cell.height < k.lowStub;
    const layers = !exposed ? [0, 3] : low ? [0, 1, 2, 3] : [0, 4, 5, 3];
    const rings: Point[][] = layers.map((layer) =>
      quad.map(([dx, dz]): Point => {
        const x = cx + dx;
        const z = cz + dz;
        const q = corner(x, z);
        const [nx, nz] = q.n;
        if (layer === 0) return [x, 0, z];
        const height = low ? cell.height + q.bed * 0.06 : cell.height + q.bed * 0.5 + 0.2 * (q.f63 - 0.5);
        if (layer > 3 || (layer === 3 && !low && exposed)) {
          // Tall exposed faces lean, step back in one strata ledge, then rise to the shoulder; the cap edge wanders per corner.
          const i1 = 0.02 + 0.05 * q.v;
          const i2 = i1 + 0.08 + 0.07 * q.f13;
          const y1 = height * (0.44 + 0.14 * q.f22);
          const [inset, y] = layer === 4 ? [i1, y1] : layer === 5 ? [i2, y1 + 0.012] : [Math.max(i2 + 0.02, Math.min(0.38, i2 + 0.06 + 0.08 * q.f31 + (q.jit - 0.5) * 0.3)), height];
          return [x + nx * inset, y, z + nz * inset];
        }
        // Low stubs: a recess and a connected shelf, then a broken sloping shoulder.
        const inset = layer === 1 ? 0.1 + 0.31 * q.v : layer === 2 ? 0.06 + 0.24 * (1 - q.v) : 0.12 + 0.29 * q.f31;
        const y = layer === 1 ? height * (0.27 + 0.21 * q.v) : layer === 2 ? height * (0.54 + 0.09 * q.v) : height;
        return [x + nx * inset, y, z + nz * inset];
      }),
    );
    for (let j = 0; j < rings.length - 1; j += 1) {
      for (let i = 0; i < 4; i += 1) {
        const n = (i + 1) % 4;
        const lower = rings[j];
        const upper = rings[j + 1];
        const a = lower?.[i];
        const b = lower?.[n];
        const c = upper?.[i];
        const d = upper?.[n];
        if (a === undefined || b === undefined || c === undefined || d === undefined) continue;
        // An east/west face's strata ledge runs toward the camera: no top edge there, so it shades as cap soil.
        const sideLedge = layers[j] === 4 && layers[j + 1] === 5 && Math.abs(b[0] - a[0]) < Math.abs(b[2] - a[2]);
        const tc = sideLedge ? noTop : c[1];
        const td = sideLedge ? noTop : d[1];
        triangle(a, c, d, tc, tc, td);
        triangle(a, d, b, tc, td, td);
      }
    }
    const bottom = rings[0];
    const top = rings.at(-1);
    if (bottom !== undefined && top !== undefined) {
      const [b0, b1, b2, b3] = bottom;
      const [t0, t1, t2, t3] = top;
      if (b0 && b1 && b2 && b3 && t0 && t1 && t2 && t3) {
        triangle(b0, b1, b2);
        triangle(b0, b2, b3);
        triangle(t0, t2, t1);
        triangle(t0, t3, t2);
      }
    }
    cell.vertexCount = count - cell.vertexStart;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions.slice(0, count * 3), 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors.slice(0, count * 3), 3));
  geometry.setAttribute('dqTop', new THREE.BufferAttribute(tops.slice(0, count), 1));
  geometry.setAttribute('lwCutaway', new THREE.BufferAttribute(new Float32Array(count), 1));
  geometry.setAttribute('dqLink', new THREE.InstancedBufferAttribute(new Float32Array(4), 4));
  geometry.computeVertexNormals();
  // Tilted cap facets shade almost as if flat (normals bent toward up) and carry no top edge.
  const normal = geometry.getAttribute('normal');
  const top = geometry.getAttribute('dqTop');
  for (let i = 0; i < count; i += 3) {
    const ny = normal.getY(i);
    if (ny < 0.5 || ny > 0.97) continue;
    const v = new THREE.Vector3(normal.getX(i) * (1 - k.capBend), ny * (1 - k.capBend) + k.capBend, normal.getZ(i) * (1 - k.capBend)).normalize();
    for (let j = i; j < i + 3; j += 1) {
      normal.setXYZ(j, v.x, v.y, v.z);
      top.setX(j, noTop);
    }
  }
  const byTile = new Map(cells.map((c) => [tileIndex(floor, c.x, c.y), c]));
  const p = geometry.getAttribute('position');
  return {
    geometry,
    topAt: (x, z) => {
      const cell = byTile.get(tileIndex(floor, Math.round(x), Math.round(-z)));
      if (cell === undefined || Math.round(x) < 0 || Math.round(x) >= floor.width) return null;
      let height = -Infinity;
      for (let i = cell.vertexStart; i < cell.vertexStart + cell.vertexCount; i += 3) {
        const [ax, ay, az, bx, by, bz, qx, qy, qz] = [p.getX(i), p.getY(i), p.getZ(i), p.getX(i + 1), p.getY(i + 1), p.getZ(i + 1), p.getX(i + 2), p.getY(i + 2), p.getZ(i + 2)];
        const den = (bz - qz) * (ax - qx) + (qx - bx) * (az - qz);
        if (den >= -1e-10) continue; // an upward face has negative XZ winding
        const u = ((bz - qz) * (x - qx) + (qx - bx) * (z - qz)) / den;
        const v = ((qz - az) * (x - qx) + (ax - qx) * (z - qz)) / den;
        const w = 1 - u - v;
        if (u >= -1e-6 && v >= -1e-6 && w >= -1e-6) height = Math.max(height, u * ay + v * by + w * qy);
      }
      return Number.isFinite(height) ? height : null;
    },
  };
};
